import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/pro-light-svg-icons/faTriangleExclamation';
import { TypedMemo } from '../../../common/types';
import { Panel, PanelBody, PanelHead } from '../../components/primitives/Panel';
import { SMLButton } from '../../components/primitives/SMLButton';
import { browseStateMessages as messages } from '../browseMessages';
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './BrowseStates.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** True for a tRPC TOO_MANY_REQUESTS answer (the server rate limit of `browse.search`). */
export function isRateLimitError(error: unknown): boolean {
  if (error === null || typeof error !== 'object' || !('data' in error)) {
    return false;
  }
  const { data } = error as { data: unknown };
  return data !== null && typeof data === 'object' && (data as { code?: unknown }).code === 'TOO_MANY_REQUESTS';
}

interface BrowseSearchErrorProps {
  error: unknown;
  onRetry: () => void;
}

/**
 * Design b4: the search failed. Before, a failed search read "No results found"; now the error
 * reaches the page, and "Try again" runs the last query again. A rate limit gets its own line.
 */
const BrowseSearchErrorComponent: React.FC<BrowseSearchErrorProps> = ({ error, onRetry }) => {
  const intl = useIntl();
  return (
    <Panel as="section" role="alert">
      <PanelHead title={intl.formatMessage(messages.resultsTitle)} />
      <PanelBody>
        <div className={styles.emptyBody}>
          <span className={clsx(styles.emptyMark, styles.emptyMarkBad)} aria-hidden="true">
            <FontAwesomeIcon icon={faTriangleExclamation} />
          </span>
          <h3 className={styles.emptyTitle}>{intl.formatMessage(messages.errorTitle)}</h3>
          <p className={styles.emptyText}>
            {intl.formatMessage(isRateLimitError(error) ? messages.errorRateLimit : messages.errorBody)}
          </p>
          <SMLButton variant="primary" onClick={onRetry}>
            {intl.formatMessage(messages.tryAgain)}
          </SMLButton>
        </div>
      </PanelBody>
    </Panel>
  );
};

export const BrowseSearchError = TypedMemo(BrowseSearchErrorComponent);
